import { useState } from 'react'
import useUIStore from '@/app/store/ui.store'
import useFeaturesStore from '@/app/store/features.store'
import useAuthStore from '@/app/store/auth.store'
import { useTranslation } from '@/shared/i18n'
import { cn } from '@/shared/utils/helpers/cn'
import { SidebarNavItem } from './SidebarNavItem'
import { SidebarSection } from './SidebarSection'
import { LanguageSwitch } from './LanguageSwitch'
import {
  Home,
  Database,
  Search,
  Plug,
  RefreshCw,
  Globe,
  Key,
  Shield,
  CheckCircle2,
  FileText,
  Lock,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  BarChart3,
  FolderOpen,
  GitBranch,
  Package,
  Gauge,
  ListChecks,
  ShieldCheck,
  Bell,
  Map,
  ClipboardCheck,
  Code2,
  Wrench,
  Sparkles,
  Layers,
} from 'lucide-react'

interface CollapsibleSectionProps {
  label: string
  icon: React.ReactNode
  color: string
  collapsed?: boolean
  feature?: string
  children: React.ReactNode
}

function CollapsibleSection({ label, icon, color, collapsed, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(true)

  if (collapsed) {
    return <>{children}</>
  }

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-semibold transition-all',
          'text-white/50 hover:bg-white/[0.04]',
        )}
      >
        <span className={cn('w-5 h-5 rounded-md flex items-center justify-center text-xs', color)}>
          {icon}
        </span>
        <span className="flex-1 text-left">{label}</span>
        {isOpen ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
      </button>
      {isOpen && (
        <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-white/[0.06] pl-2">
          {children}
        </div>
      )}
    </div>
  )
}

export function GlassSidebar() {
  const { isSidebarCollapsed, toggleSidebarCollapsed } = useUIStore()
  const isEnabled = useFeaturesStore((s) => s.isEnabled)
  const user = useAuthStore((s) => s.user)
  const { t } = useTranslation()

  const collapsed = isSidebarCollapsed

  const tenantInitials = user?.tenant
    ? user.tenant
        .replace(/^min-/, '')
        .slice(0, 2)
        .toUpperCase()
    : 'EA'

  const tenantLabel = user?.tenant ?? 'Expose API'

  const tenantTypeLabelMap: Record<string, string> = {
    CENTRAL_AUTHORITY: t('layout.sidebar.tenantTypes.CENTRAL_AUTHORITY'),
    MEMBER_SELF_MANAGED: t('layout.sidebar.tenantTypes.MEMBER_SELF_MANAGED'),
    MEMBER_HOSTED: t('layout.sidebar.tenantTypes.MEMBER_HOSTED'),
    MEMBER_HOSTED_LIGHT: t('layout.sidebar.tenantTypes.MEMBER_HOSTED_LIGHT'),
    SUBSYSTEM_HOSTED: t('layout.sidebar.tenantTypes.SUBSYSTEM_HOSTED'),
    SUBSYSTEM_INTERNAL: t('layout.sidebar.tenantTypes.SUBSYSTEM_INTERNAL'),
  }

  const tenantTypeLabel = user?.tenantType
    ? tenantTypeLabelMap[user.tenantType] ?? user.tenantType.replace(/_/g, ' ')
    : ''

  return (
    <div
      className={cn(
        'h-full flex flex-col bg-glass-sidebar border-r border-white/[0.06]',
        collapsed ? 'w-16' : 'w-[220px]',
      )}
    >
      {/* Logo area */}
      <div className="flex items-center h-14 px-3 border-b border-white/[0.06]">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center font-bold text-sm text-white shadow-[0_0_16px_rgba(37,99,235,0.3)] flex-shrink-0">
          X
        </div>
        {!collapsed && (
          <span className="ml-2.5 text-sm font-bold text-white tracking-wide">JoinXRoad</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {/* ═══ CORE: Data Pipeline ═══ */}
        <SidebarSection label={t('layout.sidebar.sections.dataPipeline')} collapsed={collapsed}>
          <SidebarNavItem to="/dashboard" icon={<Home size={18} />} label={t('layout.sidebar.items.dashboard')} collapsed={collapsed} />
          <SidebarNavItem to="/datasources" icon={<Database size={18} />} label={t('layout.sidebar.items.datasources')} collapsed={collapsed} />
          <SidebarNavItem to="/schema-explorer" icon={<Search size={18} />} label={t('layout.sidebar.items.schemaExplorer')} collapsed={collapsed} />
          <SidebarNavItem to="/endpoints" icon={<Plug size={18} />} label={t('layout.sidebar.items.endpoints')} collapsed={collapsed} />
          <SidebarNavItem to="/sync" icon={<RefreshCw size={18} />} label={t('layout.sidebar.items.sync')} collapsed={collapsed} />
        </SidebarSection>

        {/* ═══ INTEGRATION: X-Road Network ═══ */}
        {isEnabled('xroad') && (
          <CollapsibleSection
            label={t('layout.sidebar.sections.xroad')}
            icon={<Globe size={14} />}
            color="bg-sky-500/20 text-sky-400"
            collapsed={collapsed}
          >
            <SidebarNavItem to="/xroad" icon={<Home size={16} />} label={t('layout.sidebar.items.console')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/services" icon={<Globe size={16} />} label={t('layout.sidebar.items.publishedServices')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/subscriptions" icon={<Key size={16} />} label={t('layout.sidebar.items.subscriptions')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/my-access" icon={<Shield size={16} />} label={t('layout.sidebar.items.myAccess')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/catalog" icon={<BookOpen size={16} />} label={t('layout.sidebar.items.catalog')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/certificates" icon={<ShieldCheck size={16} />} label={t('layout.sidebar.items.certificates')} collapsed={collapsed} />
            <SidebarNavItem to="/xroad/consume" icon={<Plug size={16} />} label={t('layout.sidebar.items.consume')} collapsed={collapsed} />
          </CollapsibleSection>
        )}

        {/* ─── Separator ─── */}
        <div className="my-2 mx-3 border-t border-white/[0.06]" />

        {/* ═══ GOVERNANCE: Data Intelligence ═══ */}
        {isEnabled('governance') && (
          <CollapsibleSection
            label={t('layout.sidebar.sections.intelligence')}
            icon={<BarChart3 size={14} />}
            color="bg-blue-500/20 text-blue-400"
            collapsed={collapsed}
          >
            <SidebarNavItem to="/governance/hub" icon={<Map size={16} />} label={t('layout.sidebar.items.hub')} collapsed={collapsed} />
            <SidebarNavItem to="/governance/catalog" icon={<BookOpen size={16} />} label={t('layout.sidebar.items.nationalDirectory')} collapsed={collapsed} />
            <SidebarNavItem to="/governance/lineage" icon={<GitBranch size={16} />} label={t('layout.sidebar.items.lineage')} collapsed={collapsed} />
            <SidebarNavItem to="/governance/glossary" icon={<FileText size={16} />} label={t('layout.sidebar.items.glossary')} collapsed={collapsed} />
            {user?.tenantType === 'CENTRAL_AUTHORITY' && (
              <SidebarNavItem to="/governance/supervision" icon={<ShieldCheck size={16} />} label={t('layout.sidebar.items.accessSupervision')} collapsed={collapsed} />
            )}
          </CollapsibleSection>
        )}

        {/* ═══ COMPLIANCE: Qualite & Conformite ═══ */}
        {isEnabled('dq') && (
          <CollapsibleSection
            label={t('layout.sidebar.sections.quality')}
            icon={<CheckCircle2 size={14} />}
            color="bg-emerald-500/20 text-emerald-400"
            collapsed={collapsed}
          >
            <SidebarNavItem to="/dq/dashboard" icon={<Gauge size={16} />} label={t('layout.sidebar.items.dqDashboard')} collapsed={collapsed} />
            <SidebarNavItem to="/dq/rules" icon={<ListChecks size={16} />} label={t('layout.sidebar.items.rules')} collapsed={collapsed} />
            <SidebarNavItem to="/dq/gate" icon={<ShieldCheck size={16} />} label={t('layout.sidebar.items.dqGate')} collapsed={collapsed} />
            <SidebarNavItem to="/dq/alerts" icon={<Bell size={16} />} label={t('layout.sidebar.items.dqAlerts')} collapsed={collapsed} />
          </CollapsibleSection>
        )}

        {/* ═══ COMPLIANCE: Privacy & Protection ═══ */}
        {isEnabled('privacy') && (
          <CollapsibleSection
            label={t('layout.sidebar.sections.privacy')}
            icon={<Lock size={14} />}
            color="bg-violet-500/20 text-violet-400"
            collapsed={collapsed}
          >
            <SidebarNavItem to="/privacy/registry" icon={<FileText size={16} />} label={t('layout.sidebar.items.registry')} collapsed={collapsed} />
            <SidebarNavItem to="/privacy/cartography" icon={<Map size={16} />} label={t('layout.sidebar.items.cartography')} collapsed={collapsed} />
            <SidebarNavItem to="/privacy/compliance" icon={<ClipboardCheck size={16} />} label={t('layout.sidebar.items.cdpCompliance')} collapsed={collapsed} />
          </CollapsibleSection>
        )}

        {/* ═══ DATA: Registries ═══ */}
        <SidebarNavItem to="/registries" icon={<Layers size={18} />} label={t('layout.sidebar.items.registries')} collapsed={collapsed} />

        {/* ─── Separator ─── */}
        <div className="my-2 mx-3 border-t border-white/[0.06]" />

        {/* ═══ TOOLS: Tooling ═══ */}
        {isEnabled('jsonata') && (
          <CollapsibleSection
            label={t('layout.sidebar.sections.tooling')}
            icon={<Wrench size={14} />}
            color="bg-amber-500/20 text-amber-400"
            collapsed={collapsed}
          >
            <SidebarNavItem to="/jsonata/playground" icon={<Code2 size={16} />} label={t('layout.sidebar.items.playground')} collapsed={collapsed} />
            <SidebarNavItem to="/jsonata/udfs" icon={<FileText size={16} />} label={t('layout.sidebar.items.udfs')} collapsed={collapsed} />
          </CollapsibleSection>
        )}

        {/* ═══ AI: DataLens ═══ */}
        {isEnabled('datalens') && (
          <SidebarNavItem to="/datalens" icon={<Sparkles size={18} />} label={t('layout.sidebar.items.datalens')} collapsed={collapsed} />
        )}
      </nav>

      {/* Bottom: tenant info + collapse toggle */}
      <div className="border-t border-white/[0.06] p-3">
        {/* Tenant info */}
        <div className={cn('flex items-center gap-2.5 mb-3', collapsed && 'justify-center')}>
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <span className="text-[10px] font-bold text-blue-400">{tenantInitials}</span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-xs font-medium text-white/70 truncate">{tenantLabel}</div>
              {tenantTypeLabel && (
                <div className="text-[9px] text-white/30 truncate">{tenantTypeLabel}</div>
              )}
            </div>
          )}
        </div>

        {/* Language switcher */}
        <LanguageSwitch collapsed={collapsed} />

        {/* Collapse toggle */}
        <button
          onClick={toggleSidebarCollapsed}
          className={cn(
            'hidden lg:flex items-center justify-center w-full py-1.5 mt-1 rounded-lg',
            'text-white/30 hover:text-white/50 hover:bg-white/[0.04] transition-all',
          )}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </div>
  )
}
